import React, { useState, useRef, useEffect } from 'react';
import { useApp } from '../contexts/AppContext';
import { accountsAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { 
  MessageSquare, Send, Loader2, Bot, User, Sparkles,
  TrendingUp, AlertTriangle, CheckCircle
} from 'lucide-react';

const AIChatPage = () => {
  const { selectedOrg } = useApp();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);

  const suggestions = [
    "What's my current outstanding amount?",
    "Show me top spending vendors this month",
    "What's my net GST liability?",
    "Are there any overdue bills?",
    "Summarize my TDS deductions",
  ];

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const sendMessage = async (text) => {
    if (!text.trim() || !selectedOrg) return;

    const userMessage = { role: 'user', content: text, timestamp: new Date() };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setLoading(true);

    try {
      const response = await accountsAPI.chat(selectedOrg.id, text);
      const assistantMessage = {
        role: 'assistant',
        content: response.data.response,
        data_sources: response.data.data_sources,
        confidence: response.data.confidence,
        assumptions: response.data.assumptions,
        timestamp: new Date(),
      };
      setMessages(prev => [...prev, assistantMessage]);
    } catch (err) {
      const errorMessage = {
        role: 'assistant',
        content: 'Sorry, I encountered an error processing your request. Please try again.',
        confidence: 'low',
        timestamp: new Date(),
      };
      setMessages(prev => [...prev, errorMessage]);
    }

    setLoading(false);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    sendMessage(input);
  };

  const ConfidenceBadge = ({ confidence }) => {
    const variants = {
      high: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
      medium: 'bg-amber-500/10 text-amber-400 border-amber-500/20',
      low: 'bg-rose-500/10 text-rose-400 border-rose-500/20',
    };
    const icons = {
      high: CheckCircle,
      medium: AlertTriangle,
      low: AlertTriangle,
    };
    const Icon = icons[confidence] || icons.medium;

    return (
      <Badge variant="outline" className={`${variants[confidence]} text-xs`}>
        <Icon className="w-3 h-3 mr-1" />
        {confidence} confidence
      </Badge>
    );
  };

  if (!selectedOrg) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-muted-foreground">Please select an organization</p>
      </div>
    );
  }

  return (
    <div className="h-[calc(100vh-200px)] flex flex-col" data-testid="ai-chat-page">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-manrope font-bold tracking-tight flex items-center gap-2">
          <Sparkles className="w-6 h-6 text-amber-400" />
          AI Spend Analytics
        </h1>
        <p className="text-muted-foreground mt-1">
          Ask questions about your financial data
        </p>
      </div>

      {/* Chat Container */}
      <Card className="flex-1 bg-card/50 backdrop-blur-sm border-border/50 flex flex-col overflow-hidden">
        {/* Messages */}
        <ScrollArea className="flex-1 p-4" ref={scrollRef}>
          {messages.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-center space-y-6">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-emerald-500/20 to-blue-500/20 flex items-center justify-center border border-white/10">
                <Bot className="w-8 h-8 text-emerald-400" />
              </div>
              <div>
                <h3 className="text-lg font-manrope font-medium">Financial AI Assistant</h3>
                <p className="text-sm text-muted-foreground mt-1 max-w-md">
                  I can help you analyze spending patterns, understand tax liabilities, and provide financial insights.
                </p>
              </div>
              <div className="flex flex-wrap gap-2 justify-center max-w-lg">
                {suggestions.map((suggestion) => (
                  <Button
                    key={suggestion}
                    variant="outline"
                    size="sm"
                    className="text-xs"
                    onClick={() => sendMessage(suggestion)}
                    data-testid={`suggestion-${suggestion.slice(0,20)}`}
                  >
                    {suggestion}
                  </Button>
                ))}
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {messages.map((msg, i) => (
                <div
                  key={msg.id || `msg-${msg.role}-${i}`}
                  className={`flex gap-3 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  {msg.role === 'assistant' && (
                    <div className="w-8 h-8 rounded-lg bg-emerald-500/20 flex items-center justify-center shrink-0">
                      <Bot className="w-4 h-4 text-emerald-400" />
                    </div>
                  )}
                  <div className={`max-w-[80%] ${msg.role === 'user' ? 'order-first' : ''}`}>
                    <div
                      className={`rounded-xl p-4 ${
                        msg.role === 'user'
                          ? 'bg-emerald-600 text-white'
                          : 'bg-muted/50 border border-border/50'
                      }`}
                    >
                      <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                    </div>
                    {msg.role === 'assistant' && (
                      <div className="mt-2 flex flex-wrap gap-2">
                        {msg.confidence && <ConfidenceBadge confidence={msg.confidence} />}
                        {msg.data_sources?.length > 0 && (
                          <Badge variant="outline" className="text-xs">
                            Sources: {msg.data_sources.join(', ')}
                          </Badge>
                        )}
                      </div>
                    )}
                    {msg.assumptions?.length > 0 && (
                      <p className="text-xs text-muted-foreground mt-2">
                        Assumptions: {msg.assumptions.join(', ')}
                      </p>
                    )}
                  </div>
                  {msg.role === 'user' && (
                    <div className="w-8 h-8 rounded-lg bg-blue-500/20 flex items-center justify-center shrink-0">
                      <User className="w-4 h-4 text-blue-400" />
                    </div>
                  )}
                </div>
              ))}
              {loading && (
                <div className="flex gap-3">
                  <div className="w-8 h-8 rounded-lg bg-emerald-500/20 flex items-center justify-center">
                    <Bot className="w-4 h-4 text-emerald-400" />
                  </div>
                  <div className="bg-muted/50 border border-border/50 rounded-xl p-4">
                    <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
                  </div>
                </div>
              )}
            </div>
          )}
        </ScrollArea>

        {/* Input */}
        <div className="p-4 border-t border-border/50">
          <form onSubmit={handleSubmit} className="flex gap-2">
            <Input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask about your financial data..."
              className="bg-background/50 border-border/50"
              disabled={loading}
              data-testid="chat-input"
            />
            <Button
              type="submit"
              disabled={loading || !input.trim()}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="send-button"
            >
              <Send className="w-4 h-4" />
            </Button>
          </form>
          <p className="text-xs text-muted-foreground mt-2 text-center">
            AI provides insights based on your data. Always verify critical financial decisions.
          </p>
        </div>
      </Card>
    </div>
  );
};

export default AIChatPage;
